import { ScrollArea } from '@/components/shadcn/scroll-area'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import clsx from 'clsx'
import { MdTextDataSchema, MdTextUISchema } from './types'
import { Button } from '@/components/shadcn/button'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MDXEditorComponent } from '@/components/form-components/MDXEditorComponent'
import { ActionsMenu } from './ActionsMenu'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { useSubmitForm } from '../FormComponent/useSubmitForm'
import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { getComponentHeights } from '../utils'
import { printMdTextContent } from './printMdTextContent'
import { SearchInput } from '../shared/SearchInput'
import { useMdTextSearch } from './useMdTextSearch'
import { MdTextSearchNavigator } from './MdTextSearchNavigator'
import type { MdDocumentModel } from './mdDocumentModel'
import { MdTextMarkdownBody } from './MdTextMarkdownBody'

export function MdTextInlineContent({
  uiSchema,
  data,
  tileFilters,
  reloadHandler,
  documentModel,
}: {
  uiSchema: MdTextUISchema
  data: string
  tileFilters?: React.ReactNode
  reloadHandler: () => void
  documentModel: MdDocumentModel
}) {
  const { title = undefined, description = undefined, layout = { columns: 1 }, actions, emphasizeHeader } = uiSchema
  const [isEditing, setIsEditing] = useState(false)
  const [markdown, setMarkdown] = useState(() => data)
  const printableRef = useRef<HTMLDivElement>(null)
  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  const [searchValue, setSearchValue] = useState('')
  const inlineContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setMarkdown(data)
  }, [data])

  const onCancelEditing = useCallback(() => {
    setIsEditing(false)
    setMarkdown(data)
  }, [data])

  const openEditing = useCallback(() => {
    setIsEditing(true)
  }, [])

  const { tileFilterSelections } = useTileFilters()

  const editHandlerId =
    actions?.find((action) => action.type === 'edit')?.handlerId ||
    actions?.find((action) => action.id === 'edit')?.handlerId ||
    ''

  const { mutate: submitForm } = useSubmitForm(editHandlerId, tileFilterSelections, 'edit', () => setIsEditing(false))

  const handleSubmit = () => {
    submitForm({ markdown })
  }

  const handlePrintToPdf = useCallback(() => {
    printMdTextContent(printableRef.current)
  }, [])

  const { hasSearchTerm, matchCount, activeMatchIndex, goToPreviousMatch, goToNextMatch } = useMdTextSearch({
    searchValue,
    isEditing,
    content: data,
    sections: documentModel.sections,
    highlightContainerRef: inlineContentRef,
    highlightSectionBody: data,
  })

  return (
    <div ref={printableRef} className="md-text-component flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer className="md-text-component__actions">
            <SearchInput value={searchValue} onChange={setSearchValue} placeholder="Search" />
            {!isEditing && tileFilters}
            {!isEditing && (
              <ActionsMenu
                actions={actions ?? []}
                openEditing={openEditing}
                markdown={data}
                reloadHandler={reloadHandler}
                onExportPdf={handlePrintToPdf}
              />
            )}
            {isEditing && (
              <>
                <Button variant="ghost" size="sm" onClick={onCancelEditing}>
                  <IconCloseOutline size={12} />
                  <span className="ml-2">Cancel editing</span>
                </Button>
                <Button size="sm" onClick={handleSubmit}>
                  <span>Submit</span>
                </Button>
              </>
            )}
          </ComponentActionsContainer>
        </ComponentHeader>
      )}
      {isEditing ? (
        <div style={{ height: contentHeight }}>
          <MDXEditorComponent
            markdown={markdown}
            onChange={setMarkdown}
            contentEditableClassName="h-full bg-secondary/5"
            className="h-full overflow-y-auto [&_.cm-editor]:h-full [&_.cm-editor]:bg-secondary/5 [&_.mdxeditor-diff-source-wrapper]:h-[calc(100%-44px)]"
            autoFocus={true}
            diffMarkdown={data}
          />
        </div>
      ) : (
        <div className="relative" style={{ height: contentHeight }}>
          <MdTextSearchNavigator
            hasSearchTerm={hasSearchTerm}
            matchCount={matchCount}
            activeMatchIndex={activeMatchIndex}
            onPrevious={goToPreviousMatch}
            onNext={goToNextMatch}
          />
          <ScrollArea
            className={clsx('md-text-component__scroll-area', { 'pt-5': hasHeader })}
            viewportClassName="md-text-component__viewport"
            style={{ height: contentHeight }}
          >
            <div style={{ columns: layout.columns }} className="max-w-full">
              <MdTextMarkdownBody markdown={data} contentRef={inlineContentRef} />
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  )
}
